import clsx from "clsx";
import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";

import { useCaptcha } from "@smart/bridge-captcha-dom";
import {
  Button,
  FailureMessage,
  Select,
} from "@smart/itops-sb-design-system-dom";

import { CaptchaChallenge } from "./captcha-challenge";
import { rootContainerId } from "./wrapper";
import { TeamItem, Toast } from "../../types";
import { getUITranslation } from "../../utils/ui-translations";

export type SectionButtonsProps = {
  onPrevious?: (e: MouseEvent<HTMLButtonElement>) => void;
  onNext?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: (e: MouseEvent<HTMLButtonElement>) => void;
  setSidebar: (sidebar: boolean) => void;
  hasError?: string;
  isForm: boolean;
  isPreview?: boolean;
  team: TeamItem;
  disabled?: {
    back?: boolean;
    next?: boolean;
    submit?: boolean;
  };
  children: ReactNode;
  showSectionsButton: boolean;
  isTeamHidden?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  isPrint?: boolean;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  languageOptions?: Array<{ label: string; value: string }>;
  voiceConversation?: ReactNode;
  isMobile?: boolean;
  hasDigitalSignature?: boolean;
};

export const SectionButtons = ({
  onPrevious,
  onNext,
  onSubmit,
  setSidebar,
  hasError,
  team,
  isForm,
  isPreview,
  disabled,
  children,
  showSectionsButton,
  isTeamHidden,
  setToasts,
  isPrint,
  selectedLanguage,
  setSelectedLanguage,
  languageOptions,
  voiceConversation,
  isMobile,
  hasDigitalSignature,
}: SectionButtonsProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const captchaContext = useCaptcha();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      {
        root: document.getElementById(rootContainerId),
        /**
         * 70px is the height of the firm name header
         * 8px is the padding top of the sticky element
         * 1px is the triggering margin
         */
        rootMargin: isTeamHidden ? "-9px 0px 0px 0px" : "-79px 0px 0px 0px",
        threshold: 1,
      },
    );

    if (stickyRef.current) {
      observerRef.current.observe(stickyRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isTeamHidden]);

  if (isPrint) return children;

  const hasCaptchaToken = captchaContext ? captchaContext.token : true;

  const getSubmitButtonText = () => {
    if (hasDigitalSignature)
      return getUITranslation("submitAndSign", selectedLanguage);
    return getUITranslation("submit", selectedLanguage);
  };
  return (
    <div className="flex flex-col">
      {hasError && (
        <FailureMessage
          className="mb-[1.6rem]"
          action="submit form"
          contactName={team.name || "us"}
          contactLink={(team.email && `mailto:${team.email}`) || undefined}
        />
      )}
      {/* This is a sticky detection element */}
      <div
        ref={stickyRef}
        className={clsx("sticky top-0", isSticky && "h-[5.6rem]")}
      />
      {(isPreview || showSectionsButton || onNext || onPrevious) && (
        <div
          className={clsx(
            "mb-[1.6rem] bg-white z-1",
            isSticky &&
              "absolute left-0 right-0 shadow-modal @tablet:static @tablet:shadow-none",
            isSticky && !isTeamHidden && "top-[7rem]",
            isSticky && isTeamHidden && "top-[0rem]",
          )}
        >
          <div
            className={clsx(
              isSticky &&
                "mx-auto max-w-[90%] px-0 py-[1.6rem] @tablet:py-0 @tablet:max-w-full gap-large",
            )}
          >
            <div className="flex justify-between items-center gap-medium">
              <div className="flex @tablet:flex-1 justify-between gap-medium">
                <div className="flex gap-medium">
                  {onPrevious && (
                    <Button
                      variant={isMobile ? "secondarySubtle" : "primarySubtle"}
                      leftIcon="regularArrowLeft"
                      onClick={onPrevious}
                      disabled={disabled?.back}
                      dataTestId="previous-arrow-button"
                      branded
                    />
                  )}
                  {onNext && (
                    <Button
                      variant={isMobile ? "secondarySubtle" : "primarySubtle"}
                      leftIcon="regularArrowRight"
                      type={isForm ? "submit" : "button"}
                      onClick={onNext}
                      disabled={disabled?.next}
                      dataTestId="next-arrow-button"
                      branded
                    />
                  )}
                </div>
                <div className="flex gap-medium items-center">
                  {(isTeamHidden || isMobile) &&
                    languageOptions &&
                    setSelectedLanguage && (
                      <Select
                        id="select-languages-in-section-buttons"
                        placeholder="Select language"
                        options={languageOptions}
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                        compact={isMobile}
                      />
                    )}
                  {(isPreview || (!isMobile && isTeamHidden)) &&
                    voiceConversation}
                </div>
              </div>
              {isMobile && (
                <div className="flex gap-medium">
                  {!isPreview && voiceConversation}
                  {showSectionsButton && (
                    <Button
                      size="small"
                      variant="secondaryDefault"
                      rightIcon="regularBars"
                      onClick={() => setSidebar(true)}
                      branded
                      aria-label="Show Sections"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {children}
      <div className="flex flex-col gap-small">
        {onSubmit && <CaptchaChallenge setToasts={setToasts} />}
        <div className="flex flex-wrap gap-small">
          {onNext && (
            <Button
              text={getUITranslation("next", selectedLanguage)}
              type={isForm ? "submit" : "button"}
              name="submit"
              value="next"
              onClick={onNext}
              variant="primaryDefault"
              disabled={disabled?.next}
              branded
            />
          )}
          {onSubmit && (
            <Button
              text={getSubmitButtonText()}
              variant="primaryDefault"
              type={isForm ? "submit" : "button"}
              name="submit"
              value="submit"
              onClick={onSubmit}
              disabled={disabled?.submit || !hasCaptchaToken}
              branded
            />
          )}
        </div>
      </div>
    </div>
  );
};
